import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { useEdges } from 'reactflow';
import {
  ExecutionLog,
  ExecutionLogsService,
} from '../../service/ExecutionLogsService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import KitModal from '../UI/KitModal';

interface ExecutionLogsModalProps {
  show: boolean;
  onHide: () => void;
  flowId: string;
  nodeId: string;
}

const TABLE_COLUMN_COUNT = 6;

const ExecutionLogsModal = ({
  show,
  onHide,
  flowId,
  nodeId,
}: ExecutionLogsModalProps) => {
  const { user } = useAuthStore(state => state);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [fullOutputs, setFullOutputs] = useState<Record<string, string>>({});
  const [loadingOutputId, setLoadingOutputId] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const { setNotification } = useNotificationStore();
  const { setNodeState, setSaving, setSchema } = useFlowsStore(state => state);
  const edges = useEdges();

  const isDark = user?.theme === 'dark';
  const textColor = isDark ? '#D0D5DD' : '#101828';

  useEffect(() => {
    if (show && flowId && nodeId) {
      fetchLogs();
    }
    // Reset transient state whenever the modal target changes / re-opens.
    setExpandedId(null);
    setFullOutputs({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, flowId, nodeId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await ExecutionLogsService.getExecutionLogs(
        flowId,
        nodeId,
      );

      setLogs(response);
    } catch (error) {
      setNotification({
        message: 'Error fetching execution logs',
        type: 'error',
      });
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFullOutput = async (
    outputId: string,
  ): Promise<string | undefined> => {
    if (fullOutputs[outputId] !== undefined) return fullOutputs[outputId];
    try {
      setLoadingOutputId(outputId);
      const full = await ExecutionLogsService.getExecutionOutput(outputId);
      setFullOutputs(prev => ({ ...prev, [outputId]: full.output ?? '' }));
      return full.output ?? '';
    } catch (error) {
      setNotification({ message: 'Error fetching output', type: 'error' });
      return undefined;
    } finally {
      setLoadingOutputId(null);
    }
  };

  const handleToggleExpand = (log: ExecutionLog) => {
    if (expandedId === log.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(log.id);
    if (log.outputId) {
      loadFullOutput(log.outputId);
    }
  };

  const handleUseResponse = async (log: ExecutionLog) => {
    if (!log.outputId) return;
    setApplyingId(log.id);
    try {
      const output = await loadFullOutput(log.outputId);
      if (output === undefined) return;

      // Write the chosen response into the node's active output field so it
      // becomes the value downstream nodes consume. 'result' for math/scripting
      // nodes, 'text' for everything else.
      const field = log.outputType === 'result' ? 'result' : 'text';

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id: nodeId,
        flowId,
        setSaving,
        setSchema,
        changeType: 'default',
        objectData: { [field]: output },
      });

      setNotification({
        message: 'Response applied to node output',
        type: 'success',
      });
      onHide();
    } finally {
      setApplyingId(null);
    }
  };

  const headerCellSx = {
    backgroundColor: isDark ? '#1D2939' : '#F9FAFB',
    color: textColor,
  };

  return (
    <KitModal
      show={show}
      onHide={onHide}
      title="Execution Logs"
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: isDark ? '#1D2939' : '#F9FAFB',
        },
      }}
    >
      <Box>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : logs.length === 0 ? (
          <Typography
            color={isDark ? '#D0D5DD' : '#667085'}
            sx={{ textAlign: 'center', padding: '16px' }}
          >
            No execution logs found
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: isDark ? '#101828' : '#FFF',
              maxHeight: '600px',
              overflow: 'auto',
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Execution Time</TableCell>
                  <TableCell sx={headerCellSx}>Session ID</TableCell>
                  <TableCell sx={headerCellSx}>User</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Output</TableCell>
                  <TableCell sx={headerCellSx}>Error Stack</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => {
                  const isExpanded = expandedId === log.id;
                  return (
                    <Fragment key={log.id}>
                      <TableRow>
                        <TableCell sx={{ color: textColor }}>
                          {dayjs(log.createdAt).format('MMM D, YYYY h:mm A')}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: textColor,
                            fontFamily: 'monospace',
                            fontSize: '12px',
                          }}
                        >
                          {log.sessionId}
                        </TableCell>
                        <TableCell sx={{ color: textColor }}>
                          {log.userName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.isError ? 'Error' : 'Success'}
                            color={log.isError ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: '260px' }}>
                          {log.hasOutput ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: textColor,
                                  fontSize: '12px',
                                  maxWidth: '260px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {log.outputPreview}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleToggleExpand(log)}
                                sx={{
                                  alignSelf: 'flex-start',
                                  textTransform: 'none',
                                  minWidth: 'auto',
                                  padding: '0 4px',
                                  fontSize: '12px',
                                }}
                              >
                                {isExpanded ? 'Hide' : 'View'}
                              </Button>
                            </Box>
                          ) : (
                            <Typography
                              sx={{ color: isDark ? '#667085' : '#98A2B3' }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: textColor,
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            maxWidth: '200px',
                            padding: '8px',
                          }}
                        >
                          {log.errorStack && (
                            <Box
                              sx={{
                                maxHeight: '50px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                padding: '4px 8px',
                                backgroundColor: isDark
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(0, 0, 0, 0.02)',
                                borderRadius: '4px',
                                border: `1px solid ${isDark
                                  ? 'rgba(255, 255, 255, 0.1)'
                                  : 'rgba(0, 0, 0, 0.1)'
                                  }`,
                              }}
                            >
                              {log.errorStack}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>

                      {log.hasOutput && (
                        <TableRow>
                          <TableCell
                            colSpan={TABLE_COLUMN_COUNT}
                            sx={{ padding: 0, borderBottom: 'none' }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  padding: '12px 16px',
                                  backgroundColor: isDark
                                    ? 'rgba(255,255,255,0.03)'
                                    : 'rgba(0,0,0,0.02)',
                                }}
                              >
                                {loadingOutputId === log.outputId ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <CircularProgress size={16} />
                                    <Typography
                                      sx={{ color: textColor, fontSize: '13px' }}
                                    >
                                      Loading output…
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    <Box
                                      component="pre"
                                      sx={{
                                        margin: 0,
                                        maxHeight: '260px',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: 'monospace',
                                        fontSize: '12px',
                                        color: textColor,
                                        backgroundColor: isDark
                                          ? '#101828'
                                          : '#FFF',
                                        border: `1px solid ${isDark ? '#344054' : '#EAECF0'
                                          }`,
                                        borderRadius: '4px',
                                        padding: '8px',
                                      }}
                                    >
                                      {log.outputId
                                        ? fullOutputs[log.outputId] ?? ''
                                        : ''}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        marginTop: '8px',
                                      }}
                                    >
                                      <Button
                                        size="small"
                                        variant="contained"
                                        disabled={applyingId === log.id}
                                        onClick={() => handleUseResponse(log)}
                                        sx={{ textTransform: 'none' }}
                                      >
                                        {applyingId === log.id
                                          ? 'Applying…'
                                          : 'Use this response'}
                                      </Button>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </KitModal>
  );
};

export default ExecutionLogsModal;
